import { useState } from "react";
import { Image as ImageIcon, Clapperboard, CircleDashed, BarChart3, Phone } from "lucide-react";

export const PLAN_MIX = {
  social_foundation: { name: "The Foundation", posts: 8, videos: 2, stories: 0, review: false },
  social_growth: { name: "The Growth", posts: 12, videos: 4, stories: 8, review: true },
  social_signature: { name: "The Signature", posts: 20, videos: 8, stories: 12, review: true },
};

const TYPES = {
  post: { label: "Designed post", icon: ImageIcon, cls: "bg-[#FFF3F8] text-[#A8785A] border-[#E8DCCB]" },
  video: { label: "Short-form video", icon: Clapperboard, cls: "bg-[#A8785A] text-[#FFFDF7] border-[#A8785A]" },
  story: { label: "Stories", icon: CircleDashed, cls: "bg-[#FFFDF7] text-[#C48A9F] border-[#D4A0B4]" },
  report: { label: "Monthly report", icon: BarChart3, cls: "bg-[#3A2E25] text-[#FFFDF7] border-[#3A2E25]" },
  review: { label: "Analytics review", icon: Phone, cls: "bg-[#F3EAE0] text-[#3A2E25] border-[#D9CBB9]" },
};

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function buildCalendar(key) {
  const mix = PLAN_MIX[key];
  const cells = Array.from({ length: 28 }, () => []);
  const spread = (count, type, offset) => {
    for (let i = 0; i < count; i++) cells[Math.min(27, Math.floor((i + 0.5) * (28 / count)) + offset) % 28].push(type);
  };
  spread(mix.posts, "post", 0);
  spread(mix.videos, "video", 5);
  spread(mix.stories, "story", 2);
  if (mix.review) cells[13].push("review");
  cells[27].push("report");
  return cells;
}

export default function ContentCalendarPreview({ plan, lockPlan = false, compact = false }) {
  const [key, setKey] = useState(plan || "social_growth");
  const cells = buildCalendar(key);
  const mix = PLAN_MIX[key];
  return (
    <div className="glass rounded-2xl p-6 sm:p-8" data-testid="content-calendar">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="eyebrow">Sample first month</p>
          <h3 className="f-display text-2xl mt-2" data-testid="calendar-plan-name">{mix.name}</h3>
          <p className="text-xs text-[#8C7562] mt-1">{mix.posts} posts · {mix.videos} videos{mix.stories ? ` · ${mix.stories} story sets` : ""} · monthly report{mix.review ? " · analytics review" : ""}</p>
        </div>
        {!lockPlan && (
          <div className="flex gap-1 p-1 rounded-full bg-[#FFFDF7] border border-[#E8DCCB]">
            {Object.entries(PLAN_MIX).map(([k, v]) => (
              <button key={k} onClick={() => setKey(k)} data-testid={`calendar-plan-${k}`}
                className={`rounded-full px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors ${key === k ? "bg-[#A8785A] text-[#FFFDF7]" : "text-[#8C7562] hover:text-[#3A2E25]"}`}>
                {v.name.replace("The ", "")}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
        {DAYS.map((d) => <span key={d} className="text-center text-[9px] uppercase tracking-[0.2em] text-[#B9A896] pb-1">{d}</span>)}
        {cells.map((items, i) => (
          <div key={i} className={`rounded-lg border border-[#E8DCCB] bg-[#FFFDF7] ${compact ? "min-h-[52px]" : "min-h-[64px] sm:min-h-[76px]"} p-1.5 flex flex-col gap-1`} data-testid="calendar-day">
            <span className="text-[9px] text-[#B9A896] leading-none">{i + 1}</span>
            {items.map((t, j) => {
              const T = TYPES[t];
              return (
                <span key={j} title={T.label} className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[8px] sm:text-[9px] font-semibold leading-none ${T.cls}`}>
                  <T.icon size={9} /> <span className="hidden sm:inline">{T.label.split(" ")[0]}</span>
                </span>
              );
            })}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-5">
        {Object.values(TYPES).map((T) => (
          <span key={T.label} className="inline-flex items-center gap-1.5 text-[10px] text-[#8C7562]"><span className={`w-2.5 h-2.5 rounded-sm border ${T.cls}`} /> {T.label}</span>
        ))}
      </div>
      <p className="text-[11px] text-[#B9A896] mt-4">Illustrative rhythm — your real calendar is planned around your launches, promos and audience insights, and you approve every post before it goes live.</p>
    </div>
  );
}
